import { useParams, Navigate } from "react-router-dom";
import { getToken } from "./tokenStorage";
import { AuthCtx } from "./AuthProvider";
import React, { useEffect, useState, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import TeacherDisciplineChart from "./TeacherDisciplineChart";
import Collectible from "./Collectible";
import CosmeticAsset from "./CosmeticAsset";
import ItemEffect from "./ItemEffect";
import OnlineIndicator from "./OnlineIndicator";

const PIKME_ROLE = 'мастер сикс севен';

function PikmeStyles() {
  return (
    <style>{`
      @keyframes pikme-intro {
        0%   { opacity: 0; }
        8%   { opacity: 1; }
        78%  { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes pikme-67 {
        0%   { transform: scale(0) rotate(-18deg); opacity: 0; }
        28%  { transform: scale(1.35) rotate(6deg); opacity: 1; }
        52%  { transform: scale(0.92) rotate(-3deg); opacity: 1; }
        68%  { transform: scale(1) rotate(0deg); opacity: 1; }
        82%  { transform: scale(1) rotate(0deg); opacity: 1; }
        100% { transform: scale(2.2) rotate(0deg); opacity: 0; }
      }
      @keyframes pikme-spark {
        0%   { transform: translate(0,0) scale(1.2); opacity: 1; }
        100% { transform: translate(var(--dx), var(--dy)) scale(0); opacity: 0; }
      }
      @keyframes pikme-shimmer {
        0%   { background-position: -200% center; }
        100% { background-position:  200% center; }
      }
      @keyframes pikme-ring {
        0%,100% { box-shadow: 0 0 0 4px rgba(251,191,36,.7), 0 0 18px rgba(251,191,36,.35); }
        50%      { box-shadow: 0 0 0 7px rgba(251,191,36,.35), 0 0 38px rgba(251,191,36,.7); }
      }
      @keyframes pikme-hero {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes pikme-float-a {
        0%,100% { transform: translateY(0)   rotate(0deg);   opacity: .55; }
        50%      { transform: translateY(-20px) rotate(12deg); opacity: .85; }
      }
      @keyframes pikme-float-b {
        0%,100% { transform: translateY(0)   rotate(0deg);    opacity: .45; }
        50%      { transform: translateY(-15px) rotate(-10deg); opacity: .75; }
      }
      @keyframes pikme-badge-glow {
        0%,100% { filter: drop-shadow(0 0 3px rgba(251,191,36,.5)); }
        50%      { filter: drop-shadow(0 0 10px rgba(251,191,36,.9)) drop-shadow(0 0 22px rgba(249,115,22,.5)); }
      }
    `}</style>
  );
}

function PikmeIntro({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2700);
    return () => clearTimeout(t);
  }, [onDone]);

  const sparks = Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * 360;
    const dist = 90 + (i % 3) * 30;
    return {
      dx: Math.round(Math.cos((angle * Math.PI) / 180) * dist),
      dy: Math.round(Math.sin((angle * Math.PI) / 180) * dist),
      color: ['#fbbf24', '#f97316', '#fff', '#fde68a'][i % 4],
      delay: 0.28 + i * 0.035,
      size: 8 + (i % 3) * 4,
    };
  });

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'radial-gradient(ellipse at center, rgba(20,8,0,0.72) 0%, rgba(0,0,0,0.55) 100%)',
      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'pikme-intro 2.7s ease forwards',
      pointerEvents: 'none',
    }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {sparks.map((s, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: s.size, height: s.size,
            borderRadius: '50%',
            background: s.color,
            boxShadow: `0 0 ${s.size}px ${s.color}`,
            top: '50%', left: '50%',
            marginTop: -s.size / 2, marginLeft: -s.size / 2,
            '--dx': `${s.dx}px`,
            '--dy': `${s.dy}px`,
            animation: `pikme-spark 1.1s ${s.delay}s cubic-bezier(.2,.8,.4,1) forwards`,
            opacity: 0,
          }} />
        ))}
        <div style={{
          fontSize: 130,
          fontWeight: 900,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 30%, #f97316 60%, #fbbf24 80%, #fde68a 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: 'pikme-67 2.7s cubic-bezier(.34,1.56,.64,1) forwards',
          letterSpacing: '-6px',
          userSelect: 'none',
          lineHeight: 1,
        }}>
          67
        </div>
      </div>
    </div>,
    document.body
  );
}

const GRADIENTS = [
  { id: 'blue',   bg: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 60%,#7c3aed 100%)' },
  { id: 'ocean',  bg: 'linear-gradient(135deg,#06b6d4 0%,#0ea5e9 60%,#6366f1 100%)' },
  { id: 'green',  bg: 'linear-gradient(135deg,#10b981 0%,#059669 60%,#0891b2 100%)' },
  { id: 'rose',   bg: 'linear-gradient(135deg,#f43f5e 0%,#ec4899 60%,#a855f7 100%)' },
  { id: 'orange', bg: 'linear-gradient(135deg,#f97316 0%,#ef4444 60%,#dc2626 100%)' },
  { id: 'dark',   bg: 'linear-gradient(135deg,#52555e 0%,#2a2c32 60%,#1a1b1f 100%)' },
];

function getGradient(id) {
  const hash = String(id).split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return GRADIENTS[hash % GRADIENTS.length];
}

function resolveAvatar(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  return `${window.location.origin}${url}`;
}

function AvatarLightbox({ src, onClose }) {
  const [scale, setScale] = React.useState(1);
  const [pan, setPan] = React.useState({ x: 0, y: 0 });
  const dragging = React.useRef(false);
  const lastPt = React.useRef({ x: 0, y: 0 });
  const lastDist = React.useRef(null);
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const zoom = delta => setScale(s => clamp(s * delta, 1, 5));
  const reset = () => { setScale(1); setPan({ x: 0, y: 0 }); };
  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center overflow-hidden select-none"
      onClick={() => scale <= 1 && onClose()}
      onWheel={e => { e.preventDefault(); zoom(e.deltaY < 0 ? 1.12 : 0.89); }}
    >
      <button type="button" onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      {scale > 1 && (
        <button type="button" onClick={reset}
          className="absolute top-4 left-4 z-10 px-3 h-9 bg-white/20 hover:bg-white/30 rounded-full text-white text-xs font-medium transition-colors">
          1:1
        </button>
      )}
      <img
        src={src} alt=""
        className="max-w-[88vw] max-h-[88vh] rounded-xl object-contain shadow-2xl"
        style={{
          transform: `scale(${scale}) translate(${pan.x / scale}px, ${pan.y / scale}px)`,
          cursor: scale > 1 ? (dragging.current ? 'grabbing' : 'grab') : 'zoom-in',
          transition: dragging.current ? 'none' : 'transform 0.15s',
        }}
        onDoubleClick={() => scale > 1 ? reset() : setScale(2.5)}
        onClick={e => scale > 1 && e.stopPropagation()}
        onPointerDown={e => {
          if (scale <= 1) return;
          dragging.current = true;
          lastPt.current = { x: e.clientX, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={e => {
          if (!dragging.current) return;
          const dx = e.clientX - lastPt.current.x;
          const dy = e.clientY - lastPt.current.y;
          lastPt.current = { x: e.clientX, y: e.clientY };
          setPan(p => ({ x: p.x + dx, y: p.y + dy }));
        }}
        onPointerUp={() => { dragging.current = false; }}
        onTouchStart={e => {
          if (e.touches.length === 2)
            lastDist.current = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
        }}
        onTouchMove={e => {
          if (e.touches.length === 2 && lastDist.current) {
            e.preventDefault();
            const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
            zoom(dist / lastDist.current);
            lastDist.current = dist;
          }
        }}
        draggable={false}
      />
      {scale <= 1 && <p className="absolute bottom-6 text-white/30 text-[11px]">Двойной клик · прокрутка · pinch</p>}
    </div>,
    document.body
  );
}

function GroupPicker({ value, groups, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const dropRef = useRef(null);
  const [dropRect, setDropRect] = useState(null);

  const filtered = search
    ? groups.filter(g => g.toLowerCase().includes(search.toLowerCase()))
    : groups;

  const openPicker = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const w = Math.max(r.width, 220);
      setDropRect({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - w - 8), width: w });
    }
    setSearch('');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (!dropRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('pointerdown', h);
    return () => document.removeEventListener('pointerdown', h);
  }, [open]);

  const isNone = !value;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        className="flex items-center gap-0.5 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200 text-[14px] text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors outline-none focus:border-blue-400 max-w-[190px]"
      >
        <span className="truncate flex-1 text-left">{value || 'Не назначена'}</span>
        <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && dropRect && createPortal(
        <div
          ref={dropRef}
          style={{ position: 'fixed', top: dropRect.top, left: dropRect.left, minWidth: dropRect.width, zIndex: 300 }}
          className="bg-white/92 backdrop-blur-2xl rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.18)] border border-slate-100/80 overflow-hidden"
        >
          {/* Search */}
          <div className="px-2.5 pt-2.5 pb-2">
            <div className="flex items-center gap-2 bg-slate-100/90 rounded-[10px] px-2.5 py-[7px]">
              <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Поиск группы..."
                className="bg-transparent outline-none text-[13px] text-slate-700 placeholder-slate-400 w-full"
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600 shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 232 }}>
            {!search && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); }}
                className={`w-full flex items-center justify-between px-4 py-[9px] text-[13px] transition-colors hover:bg-slate-50 active:bg-slate-100 ${isNone ? 'text-blue-500 font-medium' : 'text-slate-500'}`}
              >
                <span>Не назначена</span>
                {isNone && <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>}
              </button>
            )}
            {filtered.length === 0 && search && (
              <p className="text-center text-[12px] text-slate-400 py-4">Не найдено</p>
            )}
            {filtered.map(g => {
              const active = g === value;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => { onChange(g); setOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-[9px] text-[13px] transition-colors hover:bg-slate-50 active:bg-slate-100 ${active ? 'text-blue-500 font-medium' : 'text-slate-700'}`}
                >
                  <span>{g}</span>
                  {active && <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const RARITY_COLORS = {
  chromatic: { accent: '#d946ef', pill: '#f0abfc', pillBg: 'rgba(217,70,239,0.15)' },
  legendary: { accent: '#f59e0b', pill: '#fde68a', pillBg: 'rgba(251,191,36,0.15)' },
  epic:      { accent: '#8b5cf6', pill: '#c4b5fd', pillBg: 'rgba(139,92,246,0.15)' },
  rare:      { accent: '#3b82f6', pill: '#93c5fd', pillBg: 'rgba(59,130,246,0.15)' },
  common:    { accent: '#94a3b8', pill: '#cbd5e1', pillBg: 'rgba(148,163,184,0.12)' },
};
const RARITY_LABEL = { chromatic: 'Хром.', legendary: 'Легенд.', epic: 'Эпич.', rare: 'Редкий', common: 'Обычный' };
const TYPE_LABEL   = { frame: 'Рамка', banner: 'Баннер', badge: 'Бейдж', title: 'Заголовок' };

export default function ProfilePage() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [groupSaved, setGroupSaved] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [showDevInfo, setShowDevInfo] = useState(false);
  const [showBadgeInfo, setShowBadgeInfo] = useState(false);
  const [emailCopiedKey, setEmailCopiedKey] = useState(0);
  const [adminInventory, setAdminInventory] = useState(null);
  const [adminInvOpen, setAdminInvOpen] = useState(true);
  const [isRealMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  const emailCopiedTimer = useRef(null);
  const { user: currentUser } = useContext(AuthCtx);
  const isAdmin = currentUser?.role === 'admin';
  const isOwnProfile = currentUser && String(currentUser.id) === String(userId);

  useEffect(() => {
    document.body.classList.add('account-page');
    return () => document.body.classList.remove('account-page');
  }, []);

  useEffect(() => {
    if (!isAdmin || isOwnProfile) return;
    fetch('/schedule/all-entities?source=all')
      .then(r => r.json())
      .then(data => {
        setGroups(data.groups || []);
      });
  }, [isAdmin, isOwnProfile]);

  useEffect(() => {
    if (isOwnProfile) return;
    const token = getToken();
    const url = isAdmin
      ? `/auth/admin/users/${userId}/profile`
      : `/auth/users/${userId}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setProfile(data); setLoading(false); if (data.role === PIKME_ROLE) setShowIntro(true); })
      .catch(() => setLoading(false));
  }, [userId, isAdmin, isOwnProfile]);

  useEffect(() => {
    if (!isAdmin || !adminInvOpen || adminInventory !== null) return;
    fetch(`/skrep/inventory/${userId}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAdminInventory(data))
      .catch(() => setAdminInventory([]));
  }, [isAdmin, adminInvOpen, adminInventory, userId]);


  if (isOwnProfile) return <Navigate to="/account" replace />;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
    </div>
  );

  if (!profile) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
      <p className="text-slate-500 text-sm">Пользователь не найден</p>
    </div>
  );

  const gradient = profile.profile_gradient
    ? GRADIENTS.find(g => g.id === profile.profile_gradient) || getGradient(profile.id || userId)
    : getGradient(profile.id || userId);
  const avatarSrc = resolveAvatar(profile.avatar_url);
  const avatarPos = (() => {
    const raw = profile.avatar_position;
    if (!raw) return '50% 50%';
    const [x, y] = raw.split(' ').map(Number);
    return `${x}% ${y}%`;
  })();
  const bannerSrc = profile.banner_url ? resolveAvatar(profile.banner_url) : null;
  const bannerPos = (() => {
    const raw = isRealMobile
      ? (profile.banner_position_mobile || profile.banner_position)
      : profile.banner_position;
    if (!raw) return '50% 50%';
    const [x, y] = raw.split(' ').map(Number);
    return `${x}% ${y}%`;
  })();
  const bannerZoom = isRealMobile
    ? (profile.banner_position_mobile ? (profile.banner_zoom_mobile ?? 1) : (profile.banner_zoom ?? 1))
    : (profile.banner_zoom ?? 1);
  const avatarFrame = profile.avatar_frame ? resolveAvatar(profile.avatar_frame) : null;
  const isPikme = profile.role === PIKME_ROLE;
  const roleLabel =
    isPikme ? '✦ Мастер Сикс Севен ✦' :
    profile.role === 'admin' ? 'Администратор' :
    profile.role === 'teacher' ? 'Преподаватель' : 'Студент';
  const roleColor =
    isPikme ? '' :
    profile.role === 'admin' ? 'text-violet-500' :
    profile.role === 'teacher' ? 'text-blue-500' : 'text-slate-400';
  const lastSeenText = profile.last_seen_at
    ? (() => {
        const iso = profile.last_seen_at.endsWith('Z') || profile.last_seen_at.includes('+')
          ? profile.last_seen_at
          : profile.last_seen_at + 'Z';
        return `Был(а) ${new Date(iso).toLocaleString('ru', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}`;
      })()
    : 'Не в сети';

  return (
    <div className="-mt-8 -mx-3 sm:mx-0 space-y-3">
      {isPikme && <PikmeStyles />}
      {showIntro && <PikmeIntro onDone={() => setShowIntro(false)} />}

      {/* Hero */}
      <section
        className="relative isolate rounded-none sm:rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.15)]"
      >
        {/* Фоновый слой — обрезается по скруглениям, но не обрезает рамку аватара */}
        <div
          className="absolute inset-0 rounded-none sm:rounded-2xl overflow-hidden pointer-events-none"
          style={{ zIndex: -1 }}
        >
          {isPikme ? (
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #78350f, #b45309, #d97706, #f59e0b, #b45309, #78350f)', backgroundSize: '400% 400%', animation: 'pikme-hero 5s ease infinite' }} />
          ) : bannerSrc ? (<>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${bannerSrc}')`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(22px) brightness(0.55) saturate(1.4)', transform: 'scale(1.08)', transformOrigin: 'center' }} />
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${bannerSrc}')`, backgroundSize: bannerZoom === 1 ? 'cover' : `${Math.round(bannerZoom * 100)}%`, backgroundPosition: bannerPos, backgroundRepeat: 'no-repeat' }} />
          </>) : (
            <div style={{ position: 'absolute', inset: 0, background: gradient.bg }} />
          )}
          {isPikme && (<>
          <div style={{ position:'absolute', left:'4%',  top:'12%',    fontSize:58, fontWeight:900, color:'rgba(253,230,138,.30)', animation:'pikme-float-a 3.4s ease-in-out 0s infinite',    userSelect:'none', pointerEvents:'none' }}>6</div>
          <div style={{ position:'absolute', right:'4%', top:'18%',    fontSize:68, fontWeight:900, color:'rgba(253,230,138,.24)', animation:'pikme-float-b 3.0s ease-in-out .6s infinite',   userSelect:'none', pointerEvents:'none' }}>7</div>
          <div style={{ position:'absolute', left:'16%', bottom:'10%', fontSize:32, fontWeight:900, color:'rgba(255,255,255,.16)', animation:'pikme-float-b 4.0s ease-in-out 1.1s infinite',  userSelect:'none', pointerEvents:'none' }}>67</div>
          <div style={{ position:'absolute', right:'18%',bottom:'8%',  fontSize:24, fontWeight:900, color:'rgba(255,255,255,.12)', animation:'pikme-float-a 3.7s ease-in-out 0.4s infinite',  userSelect:'none', pointerEvents:'none' }}>6</div>
          <div style={{ position:'absolute', left:'38%', top:'8%',     fontSize:20, fontWeight:900, color:'rgba(253,230,138,.18)', animation:'pikme-float-a 2.8s ease-in-out 1.5s infinite',  userSelect:'none', pointerEvents:'none' }}>7</div>
          <div style={{ position:'absolute', right:'30%',top:'14%',    fontSize:18, fontWeight:900, color:'rgba(255,255,255,.10)', animation:'pikme-float-b 3.2s ease-in-out 0.9s infinite',  userSelect:'none', pointerEvents:'none' }}>67</div>
          <div style={{ position:'absolute', left:'28%', bottom:'20%', fontSize:16, fontWeight:900, color:'rgba(253,230,138,.14)', animation:'pikme-float-b 4.2s ease-in-out 2.0s infinite',  userSelect:'none', pointerEvents:'none' }}>6</div>
          <div style={{ position:'absolute', right:'12%',bottom:'22%', fontSize:22, fontWeight:900, color:'rgba(255,255,255,.11)', animation:'pikme-float-a 3.5s ease-in-out 1.7s infinite',  userSelect:'none', pointerEvents:'none' }}>7</div>
        </>)}
        </div>
        <div className="flex flex-col items-center pt-20 pb-8 px-5 text-center" style={{ minHeight: 210 }}>
          {/* Аватар */}
          <div className="relative mb-4">
            <div className="relative" style={{ width: 80, height: 80 }}>
              <button
                type="button"
                onClick={() => avatarSrc && setLightboxOpen(true)}
                className="block w-full h-full rounded-full"
              >
                <div className={`rounded-full overflow-hidden bg-white/20 flex items-center justify-center w-full h-full ${isPikme ? '' : avatarFrame ? 'shadow-lg' : 'ring-4 ring-white/30 shadow-lg'}`}
                  style={isPikme ? { animation: 'pikme-ring 1.9s ease-in-out infinite' } : {}}>
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt=""
                      className="w-full h-full object-cover"
                      style={{ objectPosition: avatarPos }}
                    />
                  ) : (
                    <span className="text-3xl text-white font-medium select-none">
                      {(profile.fio || profile.email || '?')[0].toUpperCase()}
                    </span>
                  )}
                </div>
              </button>
              {avatarFrame && (
                <img src={avatarFrame} alt="" className="absolute pointer-events-none select-none" style={{ inset: 0, width: '100%', height: '100%', zIndex: 10, transform: `translateX(${profile.avatar_frame_offset_x ?? 0}%) translateY(${profile.avatar_frame_offset_y ?? 0}%) scale(${(profile.avatar_frame_scale ?? 100) / 100})`, transformOrigin: 'center' }} />
              )}
              {profile.equipped_badge_asset === '/badges/golden_crown.svg' && (
                <img src="/badges/golden_crown.svg" alt="Корона лидера" className="absolute pointer-events-none select-none" style={{ top: -44, left: '50%', transform: 'translateX(-50%)', width: 64, height: 64, objectFit: 'contain', zIndex: 20 }} />
              )}
            </div>
            <span className="absolute bottom-0.5 right-0.5 shadow" style={{ zIndex: 3 }}>
              <OnlineIndicator online={!!profile.is_online} device={profile.device || profile.last_device} size={16} deviceSize={24} />
            </span>
          </div>
          {lightboxOpen && avatarSrc && <AvatarLightbox src={avatarSrc} onClose={() => setLightboxOpen(false)} />}

          <h1 className="flex items-center justify-center gap-1 w-full text-[20px] font-semibold leading-tight text-center" style={{ color: 'rgba(255,255,255,0.95)', textShadow: '0 1px 3px rgba(0,0,0,0.25)' }}>
            {(() => {
              const fio = profile.fio || profile.email || '';
              const badge = (profile.is_developer && profile.dev_badge_equipped !== false) ? (
                <button type="button" onClick={() => setShowDevInfo(true)} className="focus:outline-none shrink-0" style={{ display: 'inline-flex' }}>
                  <img src="/badges/developer_status.svg" alt="Разработчик" style={{ width: 28, height: 28, display: 'block' }} />
                </button>
              ) : profile.equipped_badge_asset ? (
                <button type="button" onClick={() => setShowBadgeInfo(true)} className="focus:outline-none shrink-0" style={{ display: 'inline-flex' }}>
                  <CosmeticAsset asset={profile.equipped_badge_asset} alt="" style={{ width: 28, height: 28, display: 'block', objectFit: 'contain' }} />
                </button>
              ) : null;
              return <><span className="truncate" style={{ maxWidth: badge ? 'calc(100% - 36px)' : '88%' }}>{fio}</span>{badge}</>;
            })()}
            {showDevInfo && createPortal(
              <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setShowDevInfo(false)}>
                <style>{`@keyframes devBadgeIn{from{opacity:0;transform:scale(0.05)}to{opacity:1;transform:scale(1)}}@keyframes devBdropIn{from{opacity:0}to{opacity:1}}`}</style>
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" style={{ animation: 'devBdropIn 200ms ease both' }} />
                <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-xs w-full flex flex-col items-center gap-3"
                  style={{ animation: 'devBadgeIn 300ms cubic-bezier(0.34,1.56,0.64,1) both', transformOrigin: 'center' }}
                  onClick={e => e.stopPropagation()}>
                  <button type="button" onClick={() => setShowDevInfo(false)} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                  <img src="/badges/developer_status.svg" alt="Разработчик" style={{ width: 96, height: 96 }} />
                  <p className="text-[15px] font-semibold text-slate-800 text-center">Разработчик</p>
                  <p className="text-[13px] text-slate-500 text-center leading-relaxed">Этот бейдж выдаётся только разработчикам проекта AudAssist.</p>
                </div>
              </div>,
              document.body
            )}
            {showBadgeInfo && profile.equipped_badge_asset && createPortal(
              <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setShowBadgeInfo(false)}>
                <style>{`@keyframes devBadgeIn{from{opacity:0;transform:scale(0.05)}to{opacity:1;transform:scale(1)}}@keyframes devBdropIn{from{opacity:0}to{opacity:1}}`}</style>
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" style={{ animation: 'devBdropIn 200ms ease both' }} />
                <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-xs w-full flex flex-col items-center gap-3"
                  style={{ animation: 'devBadgeIn 300ms cubic-bezier(0.34,1.56,0.64,1) both', transformOrigin: 'center' }}
                  onClick={e => e.stopPropagation()}>
                  <button type="button" onClick={() => setShowBadgeInfo(false)} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  </button>
                  {(() => {
                    const r = profile.equipped_badge_rarity;
                    const glow = r === 'chromatic' ? 'rgba(217,70,239,0.18)' : r === 'legendary' ? 'rgba(251,191,36,0.18)' : r === 'epic' ? 'rgba(139,92,246,0.18)' : r === 'rare' ? 'rgba(59,130,246,0.15)' : 'rgba(100,116,139,0.12)';
                    return (<>
                      <div style={{ width: 128, height: 128, borderRadius: 16, background: glow, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                        <ItemEffect effectType={profile.equipped_badge_effect_type} effectParams={profile.equipped_badge_effect_params} trigger="click">
                          <CosmeticAsset asset={profile.equipped_badge_asset} alt="" style={{ width: 104, height: 104, objectFit: 'contain' }} />
                        </ItemEffect>
                      </div>
                    </>);
                  })()}
                  {profile.equipped_badge_name && (
                    <p className="text-[15px] font-semibold text-slate-800 text-center">{profile.equipped_badge_name}</p>
                  )}
                  {profile.equipped_badge_description ? (
                    <p className="text-[13px] text-slate-500 text-center leading-relaxed">{profile.equipped_badge_description}</p>
                  ) : !profile.equipped_badge_name ? (
                    <p className="text-[13px] text-slate-500 text-center leading-relaxed">Кастомный бейдж</p>
                  ) : null}
                </div>
              </div>,
              document.body
            )}
          </h1>

          <div className="flex items-center gap-0.5 mt-1.5">
            <span className={`w-2 h-2 rounded-full ${profile.is_online ? 'bg-emerald-400' : 'bg-white/40'}`} />
            <span className="text-[12px] text-white/70">
              {profile.is_online ? 'В сети' : lastSeenText}
            </span>
          </div>
        </div>
      </section>

      {/* Info */}
      <section className="rounded-2xl bg-white border border-slate-200/50 shadow-[0_2px_16px_rgba(0,0,0,0.06)] overflow-hidden mx-3 sm:mx-0">

        {/* Роль */}
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <span
            className={`text-[14px] font-medium ${roleColor}`}
            style={isPikme ? {
              background: 'linear-gradient(90deg, #fde68a, #fbbf24, #f97316, #fbbf24, #fde68a)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'pikme-shimmer 2.2s linear infinite, pikme-badge-glow 1.6s ease-in-out infinite',
              fontWeight: 700,
            } : {}}
          >
            {roleLabel}{profile.is_curator ? ' · Куратор' : ''}
          </span>
        </div>

        {/* Группа */}
        {(profile.study_group || isAdmin) && (
          <>
            <div className="h-px bg-slate-100 mx-4" />
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              {isAdmin ? (
                <div className="flex items-center gap-2 flex-1">
                  <GroupPicker
                    value={profile.study_group || ''}
                    groups={groups}
                    onChange={async g => {
                      setGroupSaved(false);
                      await fetch(`/auth/admin/users/${profile.id}/group`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                        body: JSON.stringify({ group: g }),
                      });
                      setProfile(p => ({ ...p, study_group: g }));
                      setGroupSaved(true);
                      setTimeout(() => setGroupSaved(false), 2000);
                    }}
                  />
                  {groupSaved && <span className="text-xs text-emerald-500 font-medium">Сохранено</span>}
                </div>
              ) : (
                <span className="text-[14px] text-slate-600">{profile.study_group || 'Не указана'}</span>
              )}
            </div>
          </>
        )}

        {/* Email — только для admin */}
        {isAdmin && (
          <>
            <div className="h-px bg-slate-100 mx-4" />
            <button
              type="button"
              className="flex items-center gap-3 px-4 py-3 w-full text-left relative"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
              onClick={() => {
                navigator.clipboard.writeText(profile.email).then(() => {
                  clearTimeout(emailCopiedTimer.current);
                  setEmailCopiedKey(k => k + 1);
                  emailCopiedTimer.current = setTimeout(() => setEmailCopiedKey(0), 1800);
                });
              }}
            >
              <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[14px] text-slate-600 truncate" style={{ position: 'relative' }}>
                {profile.email}
                {emailCopiedKey > 0 && (
                  <span key={emailCopiedKey} style={{
                    position: 'absolute', left: '50%', bottom: '100%',
                    transform: 'translateX(-50%)',
                    animation: 'email-copied-fly 1.8s ease-out forwards',
                    pointerEvents: 'none', whiteSpace: 'nowrap',
                    fontSize: 12, fontWeight: 600,
                    color: '#22c55e', textShadow: '0 1px 4px rgba(0,0,0,0.12)',
                  }}>Скопировано!</span>
                )}
              </span>
            </button>
            <style>{`
              @keyframes email-copied-fly {
                0%   { opacity: 0; transform: translateX(-50%) translateY(4px); }
                15%  { opacity: 1; transform: translateX(-50%) translateY(-2px); }
                70%  { opacity: 1; transform: translateX(-50%) translateY(-14px); }
                100% { opacity: 0; transform: translateX(-50%) translateY(-24px); }
              }
            `}</style>
          </>
        )}
      </section>

      {profile.collectibles?.length > 0 && (
        <section className="rounded-2xl bg-white border border-slate-200/50 shadow-[0_2px_16px_rgba(0,0,0,0.06)] mx-3 sm:mx-0 px-4 pt-3.5 pb-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 mb-3">Награды</p>
          <div className="grid grid-cols-3 gap-2.5">
            {profile.collectibles.map(c => (
              <Collectible key={c.id} src={c.image_url} name={c.name} number={c.number} rarity={c.rarity || "legendary"} />
            ))}
          </div>
        </section>
      )}

      {/* Инвентарь — только для admin */}
      {isAdmin && (
        <section className="rounded-2xl bg-white border border-slate-200/50 shadow-[0_2px_16px_rgba(0,0,0,0.06)] overflow-hidden mx-3 sm:mx-0">
          <button
            type="button"
            className="flex items-center justify-between w-full px-4 py-3 text-left"
            onClick={() => setAdminInvOpen(o => !o)}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                </svg>
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400">Инвентарь</span>
              {adminInventory !== null && (
                <span className="text-[11px] font-semibold text-slate-300">{adminInventory.length}</span>
              )}
            </div>
            <svg
              className="w-4 h-4 text-slate-300 transition-transform duration-200"
              style={{ transform: adminInvOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {adminInvOpen && (
            <div className="border-t border-slate-100 px-4 pt-3 pb-4">
              {adminInventory === null ? (
                <div className="flex justify-center py-4">
                  <div className="w-5 h-5 rounded-full border-2 border-violet-400 border-t-transparent animate-spin" />
                </div>
              ) : adminInventory.length === 0 ? (
                <p className="text-center text-[13px] text-slate-400 py-3">Инвентарь пуст</p>
              ) : (
                <div className="space-y-1.5">
                  {adminInventory.map(item => {
                    const rc = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
                    return (
                      <div key={item.item_id} className="flex items-center gap-3 rounded-xl px-3 py-2"
                        style={{ background: item.is_equipped ? rc.pillBg : 'transparent', border: `1px solid ${item.is_equipped ? rc.accent + '44' : 'transparent'}` }}>
                        <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0 bg-slate-100 flex items-center justify-center">
                          {item.asset && !item.asset.startsWith('skrep:') ? (
                            <img src={item.asset} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-slate-700 truncate leading-tight">{item.name}</p>
                          <p className="text-[11px] text-slate-400 leading-tight">{TYPE_LABEL[item.item_type] || item.item_type}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {item.duplicate_count > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-400">+{item.duplicate_count}</span>
                          )}
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: rc.pillBg, color: rc.pill }}>
                            {RARITY_LABEL[item.rarity] || item.rarity}
                          </span>
                          {item.is_equipped && (
                            <span className="w-4 h-4 rounded-full flex items-center justify-center shrink-0" style={{ background: rc.accent }}>
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l2.5 2.5L10 3"/></svg>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Аналитика преподавателя */}
      {profile.role === 'teacher' && profile.fio && (
        currentUser?.role === 'teacher' ||
        currentUser?.role === 'admin' ||
        (currentUser?.role === 'student' && currentUser?.study_group)
      ) && (
        <section className="rounded-2xl bg-white border border-slate-200/50 shadow-[0_2px_16px_rgba(0,0,0,0.06)] overflow-hidden mx-3 sm:mx-0">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <svg className="w-[15px] h-[15px] text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 leading-none">Аналитика</p>
          </div>
          <TeacherDisciplineChart
            fio={profile.fio}
            viewerGroup={currentUser?.role === 'student' ? currentUser?.study_group : undefined}
          />
        </section>
      )}

    </div>
  );
}
